import { useContext } from "react";

import { getRanaApiV1 } from "@nens/rana/api-v1";
import { UseMutationWithErrorHandlerOptions, useMutationWithErrorHandler } from "@nens/ranalib";
import { useLocalStorage } from "@nens/react/hooks";

import { useUpdateFileQueries } from "@/queries/query-file";
import { useUpdateFileAndInvalidateHistoryQueries } from "@/queries/refetch-files-and-history";
import { ProjectContext } from "@/stores/project-provider";
import { ProjectFileOrDirectory } from "@/types/api-model-simple-names";
import { getNameFromFileId } from "@/utils/get-name-from-file-id";

export type MoveProjectFileOrDirectoryArgs = {
  items: ProjectFileOrDirectory[];
  destination: string;
};

type MoveMutationOptions = UseMutationWithErrorHandlerOptions<
  unknown,
  unknown,
  MoveProjectFileOrDirectoryArgs
>;

const useMoveMutation = (options: MoveMutationOptions | undefined) => {
  const updateFileQueries = useUpdateFileQueries();
  const updateFileAndInvalidateHistoryQueries = useUpdateFileAndInvalidateHistoryQueries();
  const project = useContext(ProjectContext);
  return useMutationWithErrorHandler(
    async ({ items, destination }: MoveProjectFileOrDirectoryArgs) => {
      // Already pre move the files in the UI
      items.forEach((file) => updateFileQueries(file.id, null));

      const movedFiles = await Promise.all(
        items.map((item) => {
          const { id, type } = item;
          return getRanaApiV1().FilesApi[
            type === "file" ? "projectFileMove" : "projectDirectoryMove"
          ]({
            sourcePath: id,
            destinationPath: destination + getNameFromFileId(id),
            projectId: project.id,
          });
        })
      );

      //  Confirm the moved files in the UI and invalidate history
      await Promise.all(
        movedFiles.map((file, idx) =>
          updateFileAndInvalidateHistoryQueries({ beforeId: items[idx].id, file })
        )
      );

      return movedFiles;
    },
    options
  );
};
type MoveMutation = ReturnType<typeof useMoveMutation>;

// We do not allow mutateAsync because anything after the mutateAsync call will not wait for the confirmation modal
// But onSettled/onSuccess/onError callbacks in mutate will be called correctly
export interface MoveProjectFileOrDirectoryMutation
  extends Omit<MoveMutation, "mutateAsync" | "mutate"> {
  mutateWithConfirm: MoveMutation["mutate"];
  dontConfirmMove: boolean;
  setDontConfirmMove: (value: boolean) => void;
}

export const useMoveProjectFileOrDirectory = (
  openConfirmMoveModal: (props: { moveFiles: () => void }) => void,
  options?: MoveMutationOptions
): MoveProjectFileOrDirectoryMutation => {
  const [dontConfirmMove, setDontConfirmMove] = useLocalStorage("rana.dont-confirm-move", false);

  const { mutate, mutateAsync, ...mutateState } = useMoveMutation(options);

  const mutateWithConfirm: typeof mutate = (args, options) => {
    const moveFiles = () => mutate(args, options);
    const needsConfirmation =
      args.items.some(({ type }) => type === "directory") && !dontConfirmMove;

    if (needsConfirmation) {
      return openConfirmMoveModal({
        moveFiles,
      });
    }
    return moveFiles();
  };

  return { ...mutateState, dontConfirmMove, setDontConfirmMove, mutateWithConfirm };
};
